import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { alpha, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import BookingCardIcons from '../../components/BookingCardIcons';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { snapToShiftTime, computePreviewStartFromLeft } from '../../../../util/bookingGridUtil';
import useNow from '../../../../hooks/useNow';
import { getBookingCardHoverOverlay } from './bookingCardHoverStyles';
import { getBookingCardStatusStripeSx } from './bookingCardStatusStripes';
import {
  isInactiveBookingStatus,
  isWaitingForPaymentActionable,
  NO_SHOW_GRACE_MINUTES_AFTER_START,
} from '../../../../util/dateUtils';

const LONG_PRESS_DURATION = 1000; // ms to activate drag
const MOUSE_DRAG_THRESHOLD_PX = 3;

function resolveCardBorderColor(rawColor, theme) {
  if (!rawColor || typeof rawColor !== 'string') return theme.palette.divider;
  const trimmed = rawColor.trim().toLowerCase();
  if (trimmed === '#fff' || trimmed === '#ffffff' || trimmed === 'white') return theme.palette.grey[600];
  let hex = trimmed;
  if (hex.startsWith('#') && hex.length === 4) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  if (hex.startsWith('#') && hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    if ([r, g, b].some(n => Number.isNaN(n))) return rawColor;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (luminance > 0.75) return theme.palette.grey[600];
  }
  return rawColor;
}

// Module-level: no dependencies, never changes
const isInteractiveChild = target =>
  Boolean(target.closest('.MuiIconButton-root, [data-ignore-card-click]'));

/**
 * Generic internal booking card component.
 *
 * Handles the common visualisation logic for all entertainment types.
 * Provides props to customise look-and-feel without duplicating code.
 */
function InternalBookingCardBase({
  booking,
  // How many spots the booking occupies (participantCount or laneCount)
  getParticipantCount = bk => bk.participantCount ?? bk.laneCount ?? 0,
  color = '#000000',
  isExternallyHighlighted = false,
  renderAvailableSlots = true,
  topOffset = renderAvailableSlots ? 40 : 16,
  rowHeight = 45,
  rowSpan = 1,
  onCardClick,
  onBookingShift,
  children,
  iconProps = {},
  entertainment,
  renderExtraLeftStatusIcons,
  renderAfterName,
  shouldShowAttentionStripesExtra,
  onDragStart,
  onDragActivated,
  onDragEnd,
  onHoverStart,
  onHoverEnd,
  availableShiftTimes,
  isShiftable,
  isBookingReadyForShift,
  resourceStartOffsetMinutes = 0,
  openingGridOffset = 0,
  // controls
  showIcons = true,
  showPricingName = true,
  showDuration = true,
  showStartTimeInFirstLine = true,
  slotWidthPx = 140,
  minutesPerSlot = 30,
  borderStyle = 'solid',
  onDragPreview,
  isExternallyDragged = false,
  disableHoverEffects = false,
}) {
  const {
    _id,
    firstName,
    lastName,
    startTime,
    eventDurationMS,
    pricingName,
    presentSince,
    finishedAt,
    i,
    comment,
  } = booking;

  const cardRef = useRef(null);
  const skipOpenRef = useRef(false); // prevents modal opening after internal interactions
  const dragInfo = useRef({});
  const touchTimerRef = useRef(null); // timer for long press detection
  const availableShiftTimesRef = useRef(availableShiftTimes);
  const [touchAction, setTouchAction] = useState('auto');
  const [isGrabbed, setIsGrabbed] = useState(false);
  const dragStartTimerRef = useRef(null);

  useEffect(() => {
    availableShiftTimesRef.current = availableShiftTimes;
  }, [availableShiftTimes]);

  const participantCount = getParticipantCount(booking);
  const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
  const isEffectivelyUnpaid = !booking.fullyPaid && hasPayableAmount;
  const isAwaitingConfirmation = booking.status === 'awaitingConfirmation';
  const nowTs = useNow();
  const isWaitingForPayment = isWaitingForPaymentActionable(booking, nowTs);
  const isCheckedOut = !!finishedAt;
  const isInactiveCard = isInactiveBookingStatus(booking.status);

  const theme = useTheme();

  const extraLeftStatusIcons =
    typeof renderExtraLeftStatusIcons === 'function'
      ? renderExtraLeftStatusIcons({
          booking,
          entertainment,
          presentSince,
          finishedAt,
          isEffectivelyUnpaid,
        })
      : null;
  const hasExtraLeftStatusIcons = Boolean(extraLeftStatusIcons);

  const afterNameContent =
    typeof renderAfterName === 'function'
      ? renderAfterName({ booking, entertainment })
      : null;

  const borderColorResolved = useMemo(
    () => resolveCardBorderColor(color, theme),
    [color, theme]
  );

  const cardSolidBaseBg = useMemo(
    () =>
      theme.palette.mode === 'light' ? theme.palette.background.paper : theme.palette.grey[900],
    [theme]
  );

  const colorTintOverlay = useMemo(() => {
    return getBookingCardHoverOverlay(color, theme);
  }, [color, theme]);

  // Striped light-blue highlight for internal bookings that need operator attention
  const showAttentionStripes = useMemo(() => {
    if (isInactiveBookingStatus(booking.status)) return false;
    const now = dayjs();
    const start = booking.startTime ? dayjs(booking.startTime) : null;
    const end = booking.endTime ? dayjs(booking.endTime) : null;

    // Not marked present and already past no-show grace after start
    if (!presentSince && start?.isValid()) {
      const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
      if (now.isAfter(eligibleAt) || now.isSame(eligibleAt)) return true;
    }
    // Marked present but not paid (has payable amount and not fullyPaid)
    if (presentSince && !finishedAt && isEffectivelyUnpaid) return true;
    // Not checked out and already 30 min after end
    if (!finishedAt && end?.isValid()) {
      if (now.isAfter(end.add(15, 'minute')) || now.isSame(end.add(30, 'minute'))) return true;
    }

    if (typeof shouldShowAttentionStripesExtra === 'function') {
      return Boolean(
        shouldShowAttentionStripesExtra({
          booking,
          entertainment,
          presentSince,
          finishedAt,
          isEffectivelyUnpaid,
        })
      );
    }

    return false;
  }, [
    booking,
    presentSince,
    finishedAt,
    isEffectivelyUnpaid,
    shouldShowAttentionStripesExtra,
    entertainment,
  ]);

  // calculate position + width once for all variants
  const eventStartDate = new Date(startTime);
  const startHour = eventStartDate.getHours();
  const startMin = eventStartDate.getMinutes();
  const formattedStartTime = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(
    2,
    '0'
  )}`;
  const rawGroupCount = Number.isFinite(Number(booking.groupCount))
    ? Number(booking.groupCount)
    : null;
  const rawGroupIndex = Number.isFinite(Number(booking.groupIndex))
    ? Number(booking.groupIndex)
    : null;
  const shouldShowGroupLabel = rawGroupCount && rawGroupCount > 1;
  const resolvedGroupIndex = shouldShowGroupLabel
    ? rawGroupIndex && rawGroupIndex > 0
      ? rawGroupIndex
      : 1
    : null;
  const groupParticipantCount =
    Number.isFinite(Number(booking.groupParticipantCount)) &&
    Number(booking.groupParticipantCount) > 0
      ? Number(booking.groupParticipantCount)
      : null;
  const displayStartLabel = shouldShowGroupLabel
    ? `G${resolvedGroupIndex}${groupParticipantCount ? ` (${groupParticipantCount})` : ''} - ${formattedStartTime}`
    : formattedStartTime;
  const duration = eventDurationMS / 1000 / 60 / 60;
  const resourceOffsetMinutes = resourceStartOffsetMinutes || 0;
  const bookingWindowDistance = entertainment?.bookingWindowDistance || 15;
  const hourWidthPx = slotWidthPx * (60 / minutesPerSlot); // px per hour
  const gridOriginPx = 27; // must match BookingGridBase grid origin
  const pxPerMinute = hourWidthPx / 60;
  const totalOffsetMinutes = resourceOffsetMinutes;
  const offsetPx = totalOffsetMinutes * pxPerMinute;

  const initialOffset =
    (startHour - 9) * hourWidthPx + (startMin / 60) * hourWidthPx + gridOriginPx + offsetPx;
  const width = hourWidthPx * duration - 5;

  const validateAndShiftBooking = (newStartTime, newTimeStr) => {
    const listReady = Array.isArray(availableShiftTimesRef.current);
    let shiftPermitted = true;

    // If list not ready (still loading/null), optimistically send the request.
    if (!listReady) {
      if (onBookingShift) {
        shiftPermitted = onBookingShift(booking, newStartTime.toISOString());
      }
    } else {
      // If list ready, validate locally first (supports ['HH:MM'] or [{startTime}]).
      const arr = availableShiftTimesRef.current || [];
      const includesTime = Array.isArray(arr)
        ? typeof arr[0] === 'string'
          ? arr.includes(newTimeStr)
          : arr.some(t => t && t.startTime === newTimeStr)
        : false;
      if (includesTime) {
        if (onBookingShift) {
          shiftPermitted = onBookingShift(booking, newStartTime.toISOString());
        }
      } else {
        shiftPermitted = false;
        const availableTimes = availableShiftTimesRef.current.sort().join(', ');
        toast.error(
          `Zeitfenster nicht verfügbar. Verfügbare Startzeiten: ${availableTimes || 'keine'}.`
        );
      }
    }

    if (shiftPermitted === false) {
      // Invalid, revert position and inform user
      cardRef.current.style.left = `${dragInfo.current.initialLeft}px`;
    }
  };

  const handleMouseDown = e => {
    if (isInteractiveChild(e.target)) {
      skipOpenRef.current = true; // click originated from interactive child
    } else {
      skipOpenRef.current = false;
    }

    if (!isShiftable?.(booking)) {
      dragInfo.current.isDraggable = false;
      return;
    }
    dragInfo.current.isDraggable = true;
    e.stopPropagation();

    // For mouse drag, show available times after a short delay to distinguish from a click.
    dragStartTimerRef.current = setTimeout(() => {
      const proceed = onDragStart?.(booking, true);
      if (proceed === false) {
        dragInfo.current.isDraggable = false;
        return;
      }
      setIsGrabbed(true);
    }, 300);

    const cardRect = cardRef.current.getBoundingClientRect();
    const parent = cardRef.current.parentElement;
    const parentRect = parent.getBoundingClientRect();

    dragInfo.current = {
      ...dragInfo.current,
      hasDragged: false,
      // The offset of the mouse from the element's left edge
      dragOffset_x: e.clientX - cardRect.left,
      // The initial `left` style property value of the card
      initialLeft: initialOffset,
      startX: e.clientX,
      startY: e.clientY,
    };

    const handleMouseMove = e => {
      if (!dragInfo.current.isDraggable) return;

      if (!dragInfo.current.hasDragged) {
        const dx = e.clientX - (dragInfo.current.startX ?? e.clientX);
        const dy = e.clientY - (dragInfo.current.startY ?? e.clientY);
        const movementDistance = Math.hypot(dx, dy);
        if (movementDistance < MOUSE_DRAG_THRESHOLD_PX) {
          return;
        }
      }

      e.preventDefault(); // prevent text selection
      dragInfo.current.hasDragged = true;

      const newLeft =
        e.clientX - parentRect.left + parent.scrollLeft - dragInfo.current.dragOffset_x;

      const { snappedAbsMinutes, snappedLeft: snappedLeftRaw } = snapToShiftTime({
        pxPositionLeft: newLeft - offsetPx,
        hourWidthPx,
        gridOriginPx,
        availableShiftTimes: availableShiftTimesRef.current,
        bookingWindowDistance,
      });
      const snappedLeft = snappedLeftRaw + offsetPx;

      cardRef.current.style.left = `${snappedLeft}px`;
      cardRef.current.style.zIndex = '101';
      document.body.style.cursor = 'grabbing';
      cardRef.current.style.cursor = 'grabbing';

      // Auto-scroll logic
      const scrollThreshold = 50; // pixels from the edge
      const scrollSpeed = 10; // pixels per frame

      if (e.clientX < parentRect.left + scrollThreshold) {
        parent.scrollLeft -= scrollSpeed;
      } else if (e.clientX > parentRect.right - scrollThreshold) {
        parent.scrollLeft += scrollSpeed;
      }

      // Emit live preview start time for ordered-resource ghost rendering
      const previewStart = computePreviewStartFromLeft({
        snappedLeft: snappedLeftRaw,
        hourWidthPx,
        gridOriginPx,
        eventStartDate,
      });
      onDragPreview?.(booking, previewStart);
    };

    const handleMouseUp = e => {
      clearTimeout(dragStartTimerRef.current);
      setIsGrabbed(false);
      if (!dragInfo.current.isDraggable) return;

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      cardRef.current.style.cursor = 'grab';
      cardRef.current.style.zIndex = '2';

      if (dragInfo.current.hasDragged) {
        const finalLeft = parseFloat(cardRef.current.style.left);

        const { snappedAbsMinutes } = snapToShiftTime({
          pxPositionLeft: finalLeft - offsetPx,
          hourWidthPx,
          gridOriginPx,
          availableShiftTimes: availableShiftTimesRef.current,
          bookingWindowDistance,
        });

        let newHour = Math.floor(snappedAbsMinutes / 60);
        let newMinute = snappedAbsMinutes % 60;

        const newStartTime = new Date(eventStartDate.getTime());
        newStartTime.setHours(newHour, newMinute, 0, 0);

        const newTimeStr = `${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(
          2,
          '0'
        )}`;

        validateAndShiftBooking(newStartTime, newTimeStr);
      } else {
        // This was a click, not a drag. Only fire if the mouseup happened inside the card.
        if (
          cardRef.current &&
          cardRef.current.contains(e.target) &&
          !isInteractiveChild(e.target) &&
          !skipOpenRef.current
        ) {
          onCardClick?.(_id, color);
        }
      }
      onDragPreview?.(null, null);
      onDragEnd?.();
    };

    if (dragInfo.current.isDraggable) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleTouchStart = e => {
    // Stop propagation to prevent grid scroll
    e.stopPropagation();

    if (!isShiftable?.(booking)) {
      dragInfo.current.isDraggable = false;
      return;
    }

    const touch = e.touches[0];
    const cardRect = cardRef.current.getBoundingClientRect();
    const parent = cardRef.current.parentElement;

    dragInfo.current = {
      ...dragInfo.current,
      hasDragged: false,
      dragOffset_x: touch.clientX - cardRect.left,
      initialLeft: initialOffset,
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      parent,
      prefetching: false,
      isScrolling: false, // Flag to track if the gesture is a scroll
    };

    // Start prefetching available shift slots immediately for better UX (touch only).
    const proceedPrefetch = onDragStart?.(booking, false);
    if (proceedPrefetch === false) {
      // Drag is not allowed (e.g. missing resource). Abort early.
      dragInfo.current = {};
      return;
    }
    dragInfo.current.prefetching = true;

    // Start a timer. If it completes without the user moving, we start a drag.
    touchTimerRef.current = setTimeout(() => {
      // Timer completed: we are now officially dragging.
      dragInfo.current.isDraggable = true;
      setIsGrabbed(true);

      // Disable parent scrolling during card drag.
      if (parent) {
        parent.style.touchAction = 'none';
        parent.style.overflow = 'hidden';
      }

      // Notify that drag is now activated (show prefetched times)
      onDragActivated?.();
    }, LONG_PRESS_DURATION);
  };

  const handleTouchMove = e => {
    e.stopPropagation();

    const touch = e.touches[0];

    // Check if the user is moving their finger before the long-press timer has fired.
    if (touchTimerRef.current && !dragInfo.current.isDraggable) {
      const dx = Math.abs(touch.clientX - (dragInfo.current.startX || 0));
      const dy = Math.abs(touch.clientY - (dragInfo.current.startY || 0));

      // If they move more than a threshold, cancel the timer. This allows native scrolling.
      if (dx > 10 || dy > 10) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
        dragInfo.current.isScrolling = true; // It's a scroll, not a tap
        // User aborted before long-press → cancel prefetch if it happened
        if (dragInfo.current.prefetching) {
          onDragEnd?.();
          dragInfo.current.prefetching = false;
        }
      }
    }

    // If we are not in a draggable state, do nothing more.
    if (!dragInfo.current.isDraggable) {
      return;
    }

    if (!dragInfo.current.hasDragged) {
      // This is the first mousemove event, drag was already started in mouseDown
    }
    // If we are dragging, we must prevent the default touch action (scrolling).
    //e.preventDefault();
    dragInfo.current.hasDragged = true;

    const parent = cardRef.current.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const newLeft =
      touch.clientX - parentRect.left + parent.scrollLeft - dragInfo.current.dragOffset_x;

    const { snappedAbsMinutes, snappedLeft: snappedLeftRaw } = snapToShiftTime({
      pxPositionLeft: newLeft - offsetPx,
      hourWidthPx,
      gridOriginPx,
      availableShiftTimes: availableShiftTimesRef.current,
      bookingWindowDistance,
    });
    const snappedLeft = snappedLeftRaw + offsetPx;

    cardRef.current.style.left = `${snappedLeft}px`;
    cardRef.current.style.zIndex = '101';

    // Auto-scroll horizontally
    const scrollThreshold = 50;
    const scrollSpeed = 10;
    if (touch.clientX < parentRect.left + scrollThreshold) {
      parent.scrollLeft -= scrollSpeed;
    } else if (touch.clientX > parentRect.right - scrollThreshold) {
      parent.scrollLeft += scrollSpeed;
    }

    // Emit live preview start time for ordered-resource ghost rendering
    const previewStart = computePreviewStartFromLeft({
      snappedLeft: snappedLeftRaw,
      hourWidthPx,
      gridOriginPx,
      eventStartDate,
    });
    onDragPreview?.(booking, previewStart);
  };

  const handleTouchEnd = e => {
    setIsGrabbed(false);
    clearTimeout(touchTimerRef.current);
    touchTimerRef.current = null;

    // Restore parent scrolling
    const { parent } = dragInfo.current;
    if (parent) {
      parent.style.touchAction = '';
      parent.style.overflow = '';
    }

    // Clean up prefetch if user releases touch before long-press completes
    if (dragInfo.current.prefetching && !dragInfo.current.isDraggable) {
      onDragEnd?.();
      dragInfo.current.prefetching = false;
    }

    // --- Tap Detection Logic ---
    const touchEndTime = Date.now();
    const duration = touchEndTime - (dragInfo.current.startTime || 0);
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - (dragInfo.current.startX || 0));
    const dy = Math.abs(touch.clientY - (dragInfo.current.startY || 0));

    // A tap is a short touch with minimal movement.
    if (duration < 250 && dx < 15 && dy < 15) {
      const endTarget = document.elementFromPoint(touch.clientX, touch.clientY);
      if (
        cardRef.current &&
        cardRef.current.contains(endTarget) &&
        !isInteractiveChild(endTarget)
      ) {
        // Prevent the subsequent synthetic click from bubbling and potentially immediately
        // closing the just-opened modal (observed when the card is partially outside the viewport).
        e.preventDefault();
        e.stopPropagation();

        onCardClick?.(_id, color);
      }
      onDragEnd?.(); // Also call onDragEnd to clean up any prefetching state
      return; // It was a tap, we're done.
    }

    if (dragInfo.current.isDraggable || dragInfo.current.isScrolling) {
      // This was a drag or a scroll, prevent the synthetic browser 'click' event.
      e.preventDefault();
    }

    if (!dragInfo.current.isDraggable) {
      // This was a scroll, so just return without doing anything else
      return;
    }

    cardRef.current.style.zIndex = '2';

    if (dragInfo.current.hasDragged) {
      const finalLeft = parseFloat(cardRef.current.style.left);

      const { snappedAbsMinutes } = snapToShiftTime({
        pxPositionLeft: finalLeft - offsetPx,
        hourWidthPx,
        gridOriginPx,
        availableShiftTimes: availableShiftTimesRef.current,
        bookingWindowDistance,
      });

      let newHour = Math.floor(snappedAbsMinutes / 60);
      let newMinute = snappedAbsMinutes % 60;

      const newStartTime = new Date(eventStartDate.getTime());
      newStartTime.setHours(newHour, newMinute, 0, 0);

      const newTimeStr = `${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(
        2,
        '0'
      )}`;

      validateAndShiftBooking(newStartTime, newTimeStr);
    }

    onDragPreview?.(null, null);
    onDragEnd?.();
  };

  const handleCardClick = e => {
    // This handler is for the browser's 'click' event.
    // For draggable items, we handle the click logic in handleMouseUp/handleTouchEnd.
    // So, we only want this to fire for non-draggable items.
    if (isShiftable?.(booking)) {
      return;
    }
    if (!isInteractiveChild(e.target) && !skipOpenRef.current) {
      onCardClick?.(_id, color);
    }
  };

  const canBeShifted = isShiftable?.(booking);
  const isReady = isBookingReadyForShift?.(booking) ?? true;

  let cursorStyle = 'pointer';
  if (canBeShifted) {
    if (!isReady) {
      cursorStyle = 'pointer';
    } else {
      cursorStyle = 'grab';
    }
  }

  const isDraggedNow = isGrabbed || isExternallyDragged;
  const inactiveBackgroundColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.black, 0.54)
      : theme.palette.grey[100];
  const inactiveFilter =
    theme.palette.mode === 'dark'
      ? 'grayscale(1) brightness(0.68)'
      : 'grayscale(0.85) saturate(0.25)';
  const inactiveHoverFilter =
    theme.palette.mode === 'dark'
      ? 'grayscale(0.95) brightness(0.82)'
      : 'grayscale(0.75) saturate(0.4)';
  const safeRowSpan = (() => {
    const v = Number(rowSpan);
    if (!Number.isFinite(v) || v <= 0) return 1;
    return Math.max(1, Math.floor(v));
  })();

  const grabbedStyles = isDraggedNow
    ? {
        boxShadow: '0 0 15px 5px rgba(173, 216, 230, 0.8)',
        transition: 'box-shadow 0.15s ease-in-out, opacity 0.2s ease, filter 0.2s ease',
      }
    : {
        transition: 'box-shadow 0.15s ease-in-out, opacity 0.2s ease, filter 0.2s ease',
      };

  return (
    <Card
      ref={cardRef}
      data-booking-id={String(_id)}
      data-testid={`booking-card-${String(_id)}`}
      variant='outlined'
      onMouseEnter={() => onHoverStart?.(booking, color)}
      onMouseLeave={() => onHoverEnd?.(booking)}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'absolute',
        left: initialOffset,
        top:
          typeof topOffset === 'function'
            ? topOffset(booking, renderAvailableSlots)
            : topOffset + (i - 1) * rowHeight,
        width,
        height: `${rowHeight * safeRowSpan - 6}px`,
        // Skip layout/paint (and pause the stripe animations) for cards that are
        // scrolled out of view. This keeps horizontal scroll cost roughly constant
        // regardless of how many bookings exist.
        contentVisibility: 'auto',
        containIntrinsicSize: `${Math.max(0, Math.round(Number(width) || 0))}px ${
          rowHeight * safeRowSpan - 6
        }px`,
        zIndex: isDraggedNow ? (isGrabbed ? 101 : 100) : 2,
        cursor: cursorStyle,
        touchAction,
        overflow: 'hidden',
        ...grabbedStyles,
      }}
      sx={{
        border: '2px solid',
        borderColor: borderColorResolved,
        borderStyle,
        ...(theme.palette.mode === 'light' &&
          !isDraggedNow && {
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.14)}`,
            backgroundColor: theme.palette.background.paper,
          }),
        ...(theme.palette.mode === 'dark' &&
          !isDraggedNow && {
            backgroundColor: theme.palette.grey[900],
          }),
        ...(isExternallyHighlighted &&
          !isDraggedNow && {
            backgroundColor: cardSolidBaseBg,
            backgroundImage: colorTintOverlay,
          }),
        ...(isCheckedOut &&
          !isInactiveCard && {
            opacity: 0.45,
            filter: 'saturate(0.3)',
          }),
        ...(isInactiveCard &&
          !isDraggedNow && {
            borderColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[500],
            borderStyle: 'dashed',
            backgroundColor: inactiveBackgroundColor,
            backgroundImage: 'none',
            opacity: theme.palette.mode === 'dark' ? 0.38 : 0.45,
            filter: inactiveFilter,
          }),
        '&:hover': disableHoverEffects
          ? {}
          : isDraggedNow
            ? {}
            : {
                backgroundColor: cardSolidBaseBg,
                backgroundImage: colorTintOverlay,
                ...(isCheckedOut &&
                  !isInactiveCard && {
                    opacity: 0.65,
                    filter: 'saturate(0.5)',
                  }),
                ...(isInactiveCard && {
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[600]
                      : theme.palette.grey[500],
                  backgroundColor: inactiveBackgroundColor,
                  backgroundImage: 'none',
                  opacity: theme.palette.mode === 'dark' ? 0.52 : 0.65,
                  filter: inactiveHoverFilter,
                }),
              },
      }}
    >
      {/*
        {showNoShowIndicator && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 1,
              borderRadius: 'inherit',
              background: `repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 6px,
                rgba(211, 47, 47, 0.5) 6px,
                rgba(211, 47, 47, 0.5) 12px
              )`,
            }}
          />
        )}
      */}
      {isWaitingForPayment && (
        <Box
          sx={getBookingCardStatusStripeSx('237, 108, 2', 0.5)}
        />
      )}
      {showAttentionStripes && !isWaitingForPayment && (
        <Box
          sx={getBookingCardStatusStripeSx('33, 150, 243', 0.4)}
        />
      )}
      <CardContent
        sx={{
          p: '2px 12px !important',
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        onClick={handleCardClick}
      >
        <Stack direction='row' justifyContent='space-between' maxWidth={width} sx={{ minWidth: 0 }}>
          <Stack direction='row' alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
            {isAwaitingConfirmation && (
              <PendingIcon
                sx={{
                  color: 'warning.main',
                  verticalAlign: 'middle',
                  mr: 1.5,
                  width: 18,
                  height: 18,
                }}
              />
            )}
            {presentSince && !finishedAt && (
              <CheckCircleIcon
                sx={{
                  color: 'success.main',
                  verticalAlign: 'middle',
                  mr: isEffectivelyUnpaid || hasExtraLeftStatusIcons ? 0 : 1.5,
                  width: 18,
                  height: 18,
                }}
              />
            )}
            {finishedAt && (
              <CheckCircleIcon
                sx={{
                  color: 'error.main',
                  verticalAlign: 'middle',
                  mr: isEffectivelyUnpaid || hasExtraLeftStatusIcons ? 0 : 1.5,
                  width: 18,
                  height: 18,
                }}
              />
            )}
            {isEffectivelyUnpaid && (
              <PaidIcon
                sx={{
                  color: 'error.main',
                  verticalAlign: 'middle',
                  mr: 1.5,
                  width: 18,
                  height: 18,
                }}
              />
            )}
            {extraLeftStatusIcons}
            <Typography
              component={'div'}
              sx={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                minWidth: 0,
                flex: 1,
                lineHeight: '100%',
              }}
            >
              <b>{firstName + ' ' + lastName}</b>{afterNameContent}{' '}
              <span style={{ fontSize: '80%' }}>
                {/* row 1: optional start time */}
                {showStartTimeInFirstLine && displayStartLabel}
                {/* enforce two lines */}
                <br />
                {/* row 2: either duration/info or only start time (per ordered-resources) */}
                {!showDuration ? (
                  <>{displayStartLabel}</>
                ) : (
                  <>
                    {`${parseFloat(duration.toFixed(1))}h`}
                    {booking.resourceId?.name && ' - '}
                    {booking.resourceId?.name}
                    {showPricingName && pricingName && ' - '}
                    {showPricingName && pricingName}
                  </>
                )}
              </span>
            </Typography>
          </Stack>
          {/* Right side icons */}
          {showIcons && (
            <BookingCardIcons
              participantCount={participantCount}
              color={color}
              comment={comment}
              {...iconProps}
            />
          )}
        </Stack>
        {/* Allow extending components (e.g. menus) to be injected */}
        {children}
      </CardContent>
    </Card>
  );
}

InternalBookingCardBase.propTypes = {
  booking: PropTypes.object.isRequired,
  getParticipantCount: PropTypes.func,
  color: PropTypes.string,
  topOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  rowHeight: PropTypes.number,
  rowSpan: PropTypes.number,
  onCardClick: PropTypes.func,
  onBookingShift: PropTypes.func,
  iconProps: PropTypes.object,
  children: PropTypes.node,
  entertainment: PropTypes.object,
  renderExtraLeftStatusIcons: PropTypes.func,
  renderAfterName: PropTypes.func,
  shouldShowAttentionStripesExtra: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragActivated: PropTypes.func,
  onDragEnd: PropTypes.func,
  onDragPreview: PropTypes.func,
  availableShiftTimes: PropTypes.array,
  isShiftable: PropTypes.func,
  isBookingReadyForShift: PropTypes.func,
  resourceStartOffsetMinutes: PropTypes.number,
  openingGridOffset: PropTypes.number,
  showIcons: PropTypes.bool,
  showPricingName: PropTypes.bool,
  showDuration: PropTypes.bool,
  showStartTimeInFirstLine: PropTypes.bool,
  slotWidthPx: PropTypes.number,
  borderStyle: PropTypes.oneOf(['solid', 'dashed', 'dotted', 'double', 'none']),
  isExternallyDragged: PropTypes.bool,
  disableHoverEffects: PropTypes.bool,
};

export default InternalBookingCardBase;
